import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ShoppingCart, Plus, Minus, Trash2, ChevronLeft, CheckCircle, AlertCircle, Loader2, UtensilsCrossed, ShoppingBag } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { formatCurrency } from '@/lib/utils';
import { publicApiError, publicErrorText } from '@/lib/public-api-error';
import { FilterChips } from '@/components/ui/filter-chips';
import { Dropdown } from 'primereact/dropdown';

const publicApi = {
  get: async (url: string) => {
    const res = await fetch(`/api/v1${url}`);
    if (!res.ok) {
      const err = await res.json().catch(() => ({})) as { message?: string; code?: string };
      throw publicApiError(err);
    }
    return res.json();
  },
  post: async (url: string, data: unknown) => {
    const res = await fetch(`/api/v1${url}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({})) as { message?: string | string[]; code?: string };
      throw publicApiError(err);
    }
    return res.json();
  },
};

interface Product {
  id: string;
  name: string;
  description: string | null;
  salePrice: number;
  imageUrl: string | null;
  categoryId: string | null;
  stockQuantity: number;
  trackInventory: boolean;
}

interface Category {
  id: string;
  name: string;
  description: string | null;
}

interface Table {
  id: string;
  tableNo: string;
  capacity: number;
}

interface CartItem {
  product: Product;
  quantity: number;
}

type OrderType = 'DINE_IN' | 'TAKEAWAY';
type Step = 'menu' | 'cart' | 'checkout' | 'success';

export function MenuPage() {
  const { t } = useTranslation('pos');
  const [searchParams] = useSearchParams();
  const slug = searchParams.get('slug') || searchParams.get('t') || '';
  const tableIdParam = searchParams.get('table') || '';

  const [tenantName, setTenantName] = useState('');
  const [categories, setCategories] = useState<Category[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [tables, setTables] = useState<Table[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [activeCategoryId, setActiveCategoryId] = useState<string | null>(null);
  const [cart, setCart] = useState<CartItem[]>([]);
  const [step, setStep] = useState<Step>('menu');

  // Checkout form
  const [orderType, setOrderType] = useState<OrderType>(tableIdParam ? 'DINE_IN' : 'TAKEAWAY');
  const [selectedTable, setSelectedTable] = useState(tableIdParam);
  const [customerName, setCustomerName] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [orderId, setOrderId] = useState('');
  const [orderError, setOrderError] = useState('');

  useEffect(() => {
    if (!slug) {
      setError('Invalid menu link. No restaurant specified.');
      setLoading(false);
      return;
    }
    Promise.all([
      publicApi.get(`/public/menu?slug=${slug}`),
      publicApi.get(`/public/menu/tables?slug=${slug}`),
    ])
      .then(([menuRes, tablesRes]) => {
        setTenantName(menuRes.tenant?.name ?? '');
        setCategories(menuRes.categories ?? []);
        setProducts(menuRes.products ?? []);
        setTables(tablesRes ?? []);
        // Land on "All" (activeCategoryId = null), not the alphabetically-first
        // category, so a guest sees the full menu on arrival (T-261).
      })
      .catch(() => setError('Failed to load menu. Please try again.'))
      .finally(() => setLoading(false));
  }, [slug]);

  const filteredProducts = activeCategoryId
    ? products.filter((p) => p.categoryId === activeCategoryId)
    : products;

  const cartTotal = cart.reduce((sum, item) => sum + item.product.salePrice * item.quantity, 0);
  const cartCount = cart.reduce((sum, item) => sum + item.quantity, 0);

  function addToCart(product: Product) {
    setCart((prev) => {
      const existing = prev.find((i) => i.product.id === product.id);
      if (existing) return prev.map((i) => i.product.id === product.id ? { ...i, quantity: i.quantity + 1 } : i);
      return [...prev, { product, quantity: 1 }];
    });
  }

  function updateQty(productId: string, delta: number) {
    setCart((prev) =>
      prev
        .map((i) => i.product.id === productId ? { ...i, quantity: i.quantity + delta } : i)
        .filter((i) => i.quantity > 0)
    );
  }

  async function placeOrder() {
    setSubmitting(true);
    setOrderError('');
    try {
      const res = await publicApi.post(`/public/menu/order?slug=${slug}`, {
        type: orderType,
        tableId: orderType === 'DINE_IN' ? selectedTable || undefined : undefined,
        customerName: customerName || undefined,
        customerPhone: customerPhone || undefined,
        notes: notes || undefined,
        items: cart.map((i) => ({ productId: i.product.id, quantity: i.quantity })),
      });
      setOrderId(res.orderId);
      setStep('success');
    } catch (err: unknown) {
      setOrderError(
        publicErrorText(err, t('businessUnavailable'), 'Failed to place order. Please try again or ask a staff member for assistance.'),
      );
    } finally {
      setSubmitting(false);
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <div className="text-center">
          <Loader2 className="h-10 w-10 animate-spin text-primary mx-auto mb-3" />
          <p className="text-muted-foreground text-sm">Loading menu…</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background px-4">
        <div className="text-center">
          <AlertCircle className="h-12 w-12 text-destructive-text mx-auto mb-3" />
          <p className="text-foreground font-medium">{error}</p>
        </div>
      </div>
    );
  }

  // ── SUCCESS ────────────────────────────────────────────────────────────────
  if (step === 'success') {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-success/10 px-6 text-center">
        <CheckCircle className="h-20 w-20 text-success-text mb-4" />
        <h1 className="text-2xl font-bold text-success-text mb-2">Order Placed!</h1>
        <p className="text-muted-foreground mb-1">Your order has been received.</p>
        {orderId && <p className="text-xs text-muted-foreground mb-6">Order ID: {orderId.slice(0, 8)}</p>}
        <Button onClick={() => { setCart([]); setStep('menu'); }}>
          Order Again
        </Button>
      </div>
    );
  }

  // ── CHECKOUT ───────────────────────────────────────────────────────────────
  if (step === 'checkout') {
    return (
      <div className="max-w-lg mx-auto min-h-screen bg-background">
        <div className="sticky top-0 z-10 bg-card border-b px-4 py-3 flex items-center gap-3">
          <button onClick={() => setStep('cart')} aria-label="Back to cart" className="p-2 -ms-1 min-h-[44px] min-w-[44px] flex items-center justify-center rounded-full hover:bg-muted">
            <ChevronLeft className="h-5 w-5" />
          </button>
          <h2 className="text-lg font-semibold">Checkout</h2>
        </div>
        <div className="p-4 space-y-4">
          {/* Order type */}
          <div>
            <p className="text-sm font-medium mb-2">Order Type</p>
            <div className="grid grid-cols-2 gap-2">
              {(['DINE_IN', 'TAKEAWAY'] as OrderType[]).map((t) => (
                <button
                  key={t}
                  onClick={() => setOrderType(t)}
                  className={`py-3 rounded-xl border-2 text-sm font-medium transition ${orderType === t ? 'border-primary bg-primary/10 text-primary-text' : 'border-border text-muted-foreground'}`}
                >
                  <span className="inline-flex items-center justify-center gap-2">
                    {t === 'DINE_IN' ? <UtensilsCrossed className="h-4 w-4" /> : <ShoppingBag className="h-4 w-4" />}
                    {t === 'DINE_IN' ? 'Dine In' : 'Takeaway'}
                  </span>
                </button>
              ))}
            </div>
          </div>

          {/* Table selector */}
          {orderType === 'DINE_IN' && tables.length > 0 && (
            <div>
              <p className="text-sm font-medium mb-2">Select Table</p>
              <Dropdown
                value={selectedTable}
                onChange={(e) => setSelectedTable(e.value)}
                options={[
                  { label: '-- Select a table --', value: '' },
                  ...tables.map((tb) => ({ label: `Table #${tb.tableNo} (seats ${tb.capacity})`, value: tb.id })),
                ]}
                optionLabel="label"
                optionValue="value"
                placeholder="-- Select a table --"
                className="w-full"
              />
            </div>
          )}

          {/* Customer info */}
          <div>
            <p className="text-sm font-medium mb-2">Your Details (Optional)</p>
            <Input
              value={customerName}
              onChange={(e) => setCustomerName(e.target.value)}
              placeholder="Name"
              className="w-full rounded-lg mb-2"
            />
            <Input
              dir="ltr"
              value={customerPhone}
              onChange={(e) => setCustomerPhone(e.target.value)}
              placeholder="Phone"
              type="tel"
              className="w-full rounded-lg"
            />
          </div>

          {/* Notes */}
          <div>
            <p className="text-sm font-medium mb-2">Special Instructions</p>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Any special requests?"
              rows={3}
              className="resize-none"
            />
          </div>

          {/* Order summary */}
          <div className="bg-muted rounded-xl p-4">
            <p className="text-sm font-semibold mb-3">Order Summary</p>
            {cart.map((item) => (
              <div key={item.product.id} className="flex justify-between text-sm mb-1">
                <span>{item.quantity}× {item.product.name}</span>
                <span>{formatCurrency(item.product.salePrice * item.quantity)}</span>
              </div>
            ))}
            <div className="border-t mt-2 pt-2 flex justify-between font-semibold">
              <span>Total</span>
              <span>{formatCurrency(cartTotal)}</span>
            </div>
          </div>

          <Button
            className="w-full py-3 text-base"
            onClick={placeOrder}
            disabled={submitting}
          >
            {submitting ? <><Loader2 className="h-4 w-4 animate-spin me-2" />Placing Order…</> : `Place Order · ${formatCurrency(cartTotal)}`}
          </Button>

          {orderError && (
            <div className="flex items-start gap-3 bg-destructive/10 border border-destructive/30 rounded-xl p-4">
              <AlertCircle className="h-5 w-5 text-destructive-text flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-destructive-text">Order failed</p>
                <p className="text-sm text-destructive-text mt-0.5">{orderError}</p>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  // ── CART ───────────────────────────────────────────────────────────────────
  if (step === 'cart') {
    return (
      <div className="max-w-lg mx-auto min-h-screen bg-background">
        <div className="sticky top-0 z-10 bg-card border-b px-4 py-3 flex items-center gap-3">
          <button onClick={() => setStep('menu')} aria-label="Back to menu" className="p-2 -ms-1 min-h-[44px] min-w-[44px] flex items-center justify-center rounded-full hover:bg-muted">
            <ChevronLeft className="h-5 w-5" />
          </button>
          <h2 className="text-lg font-semibold">Your Cart</h2>
        </div>

        {cart.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-64 text-muted-foreground">
            <ShoppingCart className="h-10 w-10 mb-2" />
            <p className="text-sm">Your cart is empty</p>
          </div>
        ) : (
          <div className="p-4 space-y-3">
            {cart.map((item) => (
              <div key={item.product.id} className="flex items-center gap-3 p-3 border rounded-xl">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{item.product.name}</p>
                  <p className="text-xs text-muted-foreground">{formatCurrency(item.product.salePrice)} each</p>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => updateQty(item.product.id, -1)}
                    aria-label={item.quantity === 1 ? 'Remove item' : 'Decrease quantity'}
                    className="h-10 w-10 min-h-[40px] min-w-[40px] rounded-full bg-muted flex items-center justify-center active:scale-95"
                  >
                    {item.quantity === 1 ? <Trash2 className="h-4 w-4 text-destructive-text" /> : <Minus className="h-4 w-4" />}
                  </button>
                  <span className="text-sm font-semibold w-5 text-center">{item.quantity}</span>
                  <button
                    onClick={() => updateQty(item.product.id, 1)}
                    aria-label="Increase quantity"
                    className="h-10 w-10 min-h-[40px] min-w-[40px] rounded-full bg-primary/10 text-primary-text flex items-center justify-center active:scale-95"
                  >
                    <Plus className="h-4 w-4" />
                  </button>
                </div>
                <span className="text-sm font-semibold w-20 text-end">{formatCurrency(item.product.salePrice * item.quantity)}</span>
              </div>
            ))}

            <div className="border-t pt-3 flex justify-between font-semibold text-base">
              <span>Total</span>
              <span>{formatCurrency(cartTotal)}</span>
            </div>

            <Button className="w-full py-3 text-base" onClick={() => setStep('checkout')}>
              Proceed to Checkout
            </Button>
          </div>
        )}
      </div>
    );
  }

  // ── MENU ───────────────────────────────────────────────────────────────────
  return (
    <div className="max-w-lg mx-auto min-h-screen bg-background">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-card border-b">
        <div className="px-4 py-3 flex items-center justify-between">
          <h1 className="text-lg font-bold">{tenantName}</h1>
          <button
            onClick={() => setStep('cart')}
            className="relative p-2 rounded-full bg-primary text-primary-foreground"
          >
            <ShoppingCart className="h-5 w-5" />
            {cartCount > 0 && (
              <span className="absolute -top-1 -end-1 h-5 w-5 rounded-full bg-destructive text-destructive-foreground text-xs flex items-center justify-center font-bold">
                {cartCount}
              </span>
            )}
          </button>
        </div>

        {categories.length > 0 && (
          <div className="px-4 pb-3">
            <FilterChips
              chips={[
                { key: 'all', label: 'All' },
                ...categories.map((cat) => ({ key: cat.id, label: cat.name })),
              ]}
              value={activeCategoryId ?? 'all'}
              onChange={(key) => setActiveCategoryId(key === 'all' ? null : key)}
            />
          </div>
        )}
      </div>

      {/* Product grid */}
      <div className={`p-4 ${cartCount > 0 ? 'pb-28' : ''}`}>
        {filteredProducts.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground">
            <p className="text-sm">No items available in this category</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-3">
            {filteredProducts.map((product) => {
              const cartItem = cart.find((i) => i.product.id === product.id);
              return (
                <div key={product.id} className="border rounded-xl overflow-hidden">
                  {product.imageUrl ? (
                    <img src={product.imageUrl} alt={product.name} className="w-full h-28 object-cover" />
                  ) : (
                    <div className="w-full h-28 bg-muted flex items-center justify-center">
                      <UtensilsCrossed className="h-5 w-5 text-muted-foreground" />
                    </div>
                  )}
                  <div className="p-3">
                    <p className="text-sm font-semibold leading-tight mb-0.5 line-clamp-1">{product.name}</p>
                    {product.description && (
                      <p className="text-xs text-muted-foreground line-clamp-1 mb-1">{product.description}</p>
                    )}
                    <p className="text-sm font-bold text-primary-text mb-2">{formatCurrency(product.salePrice)}</p>
                    {cartItem ? (
                      <div className="flex items-center justify-between">
                        <button onClick={() => updateQty(product.id, -1)} aria-label="Decrease quantity" className="h-9 w-9 min-h-[36px] min-w-[36px] rounded-full bg-muted flex items-center justify-center active:scale-95">
                          <Minus className="h-4 w-4" />
                        </button>
                        <span className="text-sm font-bold">{cartItem.quantity}</span>
                        <button onClick={() => updateQty(product.id, 1)} aria-label="Increase quantity" className="h-9 w-9 min-h-[36px] min-w-[36px] rounded-full bg-primary text-primary-foreground flex items-center justify-center active:scale-95">
                          <Plus className="h-4 w-4" />
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={() => addToCart(product)}
                        className="w-full flex items-center justify-center gap-1 bg-primary text-primary-foreground rounded-lg py-2.5 min-h-[40px] text-sm font-medium active:scale-95"
                      >
                        <Plus className="h-4 w-4" />
                        Add
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Floating cart bar */}
      {cartCount > 0 && (
        <div className="fixed bottom-0 inset-x-0 p-4 pb-[calc(1rem+env(safe-area-inset-bottom))] max-w-lg mx-auto">
          <button
            onClick={() => setStep('cart')}
            className="w-full bg-primary text-primary-foreground rounded-xl py-4 min-h-[56px] flex items-center justify-between px-5 shadow-lg text-sm font-semibold active:scale-[0.98]"
          >
            <span className="bg-primary-foreground/20 rounded-lg px-2 py-0.5">{cartCount} {cartCount === 1 ? t('itemSingular') : t('itemPlural')}</span>
            <span>View Cart</span>
            <span>{formatCurrency(cartTotal)}</span>
          </button>
        </div>
      )}
    </div>
  );
}
